/**
 * 資料ダウンロードフォーム (modely/register-lead) の制御
 *
 * 旧 Concrete (Vue + Firebase SDK) の挙動を素の JS で再現する。
 * - 必須項目 ([required]) が未入力 → ボタン disabled + サブラベル「未入力項目があります」
 * - すべて入力済み           → ボタン enabled  + サブラベル「ダウンロードリンクをお送りします」
 * - 検討状況 = 制作依頼を検討中 のときだけ「制作完成希望時期」radio を表示
 * - submit 時:
 *   1) GA4 dataLayer push (white_paper_downloaded)
 *   2) Firebase Callable Function `sendmail` を呼んで申込者宛と社内宛にメール送信
 *   3) 7 日以内に同メールで申込み済みでなければ `createLead` を呼んで Zapier → Pipedrive 登録
 *   4) フォームを「ダウンロードリンクを送信しました」表示に差し替え
 * - エラー時は alert + 送信中状態を解除
 *
 * Firebase Callable Functions は本体のリクエストを `{data: payload}` で包む独自フォーマットを
 * 要求する。SDK は使わず素の fetch で同じ形式を出す。
 *
 * 複数ファイルモード (form[data-multi]) — /guide/document/ で使う:
 * - 資料は name="lead_papers" のチェックボックス（form 属性でフォームに結び付いた外の要素でもよい）で選ぶ。
 *   value が資料名、data-file-url がファイルの URL
 * - 1つも選んでいなければ送信できない（サブラベル「資料を選んでください」）
 * - 申込者宛・社内宛のメールは資料ごとに 1 通ずつ、1 ファイルのときと同じ本文で送る（メールの仕組みは変えない。
 *   東さん 2026-10-27 の判断）。createLead は 1 回で、paperTitle は資料名を「、」で繋ぐ
 * - 途中のファイルで失敗したら、送れたファイルを覚えておき、もう一度送るときは残りだけ送る（同じメールを二重に出さない）。
 *   GA4 の white_paper_downloaded も、送れたファイルだけ送った直後に push する
 * - [data-register-lead-pick="<checkbox の id>"] のボタンでチェックを入れてフォームへスクロールできる
 */

const SENDMAIL_URL    = "https://asia-northeast1-melta-automations.cloudfunctions.net/sendmail";
const CREATE_LEAD_URL = "https://asia-northeast1-melta-automations.cloudfunctions.net/createLead";
const SERVICE_NAME    = "モデリー";

function getCookie(name) {
  const target = name + "=";
  for (const part of document.cookie.split("; ")) {
    if (part.startsWith(target)) return decodeURIComponent(part.slice(target.length));
  }
  return "";
}

function setCookie(name, value, days, path) {
  const expires = new Date(Date.now() + days * 86400 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=${path || "/"}; SameSite=Lax`;
}

async function callCallable(url, payload) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data: payload }),
  });
  let json = null;
  try { json = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok || (json && json.error)) {
    const msg = (json && json.error && json.error.message) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return json ? json.result : null;
}

function buildConfirmSubject(paperTitle) {
  return `【${SERVICE_NAME}】${paperTitle} ダウンロードリンク`;
}

function buildConfirmBody(name, paperTitle, url) {
  return `${name}様

このたびは、株式会社メルタの運営する${SERVICE_NAME}の資料
「${paperTitle}」をご希望いただき誠にありがとうございます。
以下のURLから資料をダウンロードしてください。
${url}

ご不明な点がありましたら、 3d-modely@melta.co.jp までご気軽にお尋ねください。

株式会社メルタ
      `;
}

function buildGroupSubject(company, name, paperTitle) {
  return `【${SERVICE_NAME}】${company} ${name}様が資料をダウンロードしました`;
}

function buildGroupBody(d) {
  const timingLine = d.timing ? `希望制作完了時期: ${d.timing}` : "";
  return `
資料ダウンロードがありました
資料名: ${d.paperTitle}
氏名: ${d.name}
会社名・団体名: ${d.company}
部署名: ${d.department}
メールアドレス: ${d.email}
電話番号: ${d.phone}
検討状況: ${d.progress}
utm_source: ${d.utmSource}
utm_campaign: ${d.utmCampaign}
utm_medium: ${d.utmMedium}
${timingLine}
`;
}

export function initRegisterLead() {
  const forms = document.querySelectorAll(".bt_register-lead__form");
  forms.forEach((form) => {
    const button = form.querySelector("[data-register-lead-submit]");
    const sub    = form.querySelector("[data-register-lead-sublabel]");
    const label  = form.querySelector("[data-register-lead-label]");
    if (!button) return;

    const multi = form.hasAttribute("data-multi");
    // form 属性で外から結び付いた要素も拾うため、複数ファイルモードは form.elements から集める
    const requiredFields = multi
      ? Array.from(form.elements).filter((el) => el.required)
      : Array.from(form.querySelectorAll("[required]"));
    const timingField    = form.querySelector(".bt_register-lead__field--timing");
    const completedEl    = form.parentElement?.querySelector("[data-register-lead-completed]");
    const completedEmail = completedEl?.querySelector("[data-register-lead-completed-email]");

    const papers = () => multi
      ? Array.from(form.elements).filter((el) => el.name === "lead_papers" && el.type === "checkbox")
      : [];
    const pickedPapers = () => papers().filter((p) => p.checked);

    const isValid = () => {
      if (multi && pickedPapers().length === 0) return false;
      if (!requiredFields.length) return true;
      for (const field of requiredFields) {
        if (field.type === "radio") {
          const anyChecked = form.querySelector(
            `input[type="radio"][name="${CSS.escape(field.name)}"]:checked`
          );
          if (!anyChecked) return false;
          continue;
        }
        if (!field.value || field.value.trim() === "") return false;
        if (typeof field.checkValidity === "function" && !field.checkValidity()) {
          return false;
        }
      }
      return true;
    };

    const countEl      = form.querySelector("[data-register-lead-count]");
    const pickedListEl = form.querySelector("[data-register-lead-picked-list]");
    const pickedEmpty  = form.querySelector("[data-register-lead-picked-empty]");

    const updatePicked = () => {
      if (!multi) return;
      const picked = pickedPapers();
      if (countEl) countEl.textContent = String(picked.length);
      if (pickedListEl) {
        pickedListEl.replaceChildren(...picked.map((p) => {
          const li = document.createElement("li");
          li.textContent = p.value;
          return li;
        }));
      }
      if (pickedEmpty) pickedEmpty.hidden = picked.length > 0;
      // 比較表の行にも選択中の印を付ける
      papers().forEach((p) => {
        const row = document.querySelector(`[data-dl-row="${CSS.escape(p.id)}"]`);
        if (row) row.classList.toggle("is-picked", p.checked);
      });
    };

    let sending = false;   // 送信中は入力やチェックが変わってもボタンを押せるように戻さない（二重送信を防ぐ）

    const update = () => {
      const valid = isValid();
      button.disabled = sending || !valid;
      if (sub) {
        if (multi && pickedPapers().length === 0) sub.textContent = "資料を選んでください";
        else sub.textContent = valid ? "ダウンロードリンクをお送りします" : "未入力項目があります";
      }
      updatePicked();
    };

    // 検討状況 = 制作依頼を検討中 のときだけ timing を表示。それ以外は選択をクリア。
    const updateTimingVisibility = () => {
      if (!timingField) return;
      const selected = form.querySelector('input[name="lead_progress"]:checked');
      const show = !!(selected && selected.value === "制作依頼を検討中");
      timingField.hidden = !show;
      if (!show) {
        form.querySelectorAll('input[name="lead_timing"]').forEach((r) => { r.checked = false; });
      }
    };

    // 複数ファイルモードで送信に成功したファイル。メールアドレス（小文字）ごとに覚える。
    // 途中失敗のあとアドレスを直して送り直したときは、新しいアドレスには全ファイル送るため
    const sentByEmail = new Map();

    const refresh = () => { update(); updateTimingVisibility(); };
    form.addEventListener("input",  refresh);
    form.addEventListener("change", refresh);
    // フォームの外にあるチェックボックス (form 属性で結び付いたもの) のイベントはフォームまで上がらないので個別に拾う
    papers().forEach((p) => { if (!form.contains(p)) p.addEventListener("change", refresh); });
    if (multi) {
      document.querySelectorAll("[data-register-lead-pick]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const target = document.getElementById(btn.getAttribute("data-register-lead-pick") || "");
          if (!target || target.form !== form) return;
          if (target.disabled) return;   // 送信済み
          target.checked = true;
          refresh();
          form.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      });
    }
    refresh();

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (sending || button.disabled) return;
      sending = true;

      const val = (sel) => form.querySelector(sel)?.value?.trim() || "";
      const name        = val('[name="lead_name"]');
      const company     = val('[name="lead_company"]');
      const department  = val('[name="lead_department"]');
      const email       = val('[name="lead_email"]');
      const phone       = val('[name="lead_phone"]');
      const progress    = form.querySelector('input[name="lead_progress"]:checked')?.value || "";
      const timing      = form.querySelector('input[name="lead_timing"]:checked')?.value || "";

      const picked      = pickedPapers().map((p) => ({ title: p.value, url: p.dataset.fileUrl || "" }));
      const paperTitle  = multi ? picked.map((p) => p.title).join("、") : (form.dataset.paperTitle || "ガイドブック");
      const fileUrl     = form.dataset.fileUrl || "";
      // メールは資料ごとに 1 通ずつ (1 ファイルのときと同じ本文)
      const mails       = multi ? picked : [{ title: paperTitle, url: fileUrl }];

      const utmSource   = getCookie("utm_source");
      const utmCampaign = getCookie("utm_campaign");
      const utmMedium   = getCookie("utm_medium");

      // GA4 (GTM 経由)。1 イベント = 1 ファイル。複数ファイルモードではメールが送れた直後に 1 ファイルずつ push する
      const pushDownloaded = (title) => {
        if (!window.dataLayer) return;
        window.dataLayer.push({
          event: "white_paper_downloaded",
          event_category: "Lead",
          paper_title: title,
          event_value: 1,
        });
      };
      if (!multi) pushDownloaded(paperTitle);

      // 送信中状態
      button.disabled = true;
      if (label) label.textContent = "送信中…";
      if (sub)   sub.textContent   = "ダウンロードリンクをお送りします";

      try {
        const emailKey = email.toLowerCase();
        if (multi && !sentByEmail.has(emailKey)) sentByEmail.set(emailKey, new Set());
        const sentUrls = multi ? sentByEmail.get(emailKey) : null;
        for (const mail of mails) {
          if (multi && sentUrls.has(mail.url)) continue;   // このアドレスに前回の送信で届いているファイルは飛ばす
          await callCallable(SENDMAIL_URL, {
            userEmail: email,
            confirmSubject: buildConfirmSubject(mail.title),
            confirmBody:    buildConfirmBody(name, mail.title, mail.url),
            groupSubject:   buildGroupSubject(company, name, mail.title),
            groupBody:      buildGroupBody({
              paperTitle: mail.title, name, company, department, email, phone, progress,
              utmSource, utmCampaign, utmMedium, timing,
            }),
          });
          if (multi) {
            sentUrls.add(mail.url);
            pushDownloaded(mail.title);
          }
        }

        // 完了表示に切替
        if (completedEl) {
          if (completedEmail) completedEmail.textContent = email;
          form.hidden = true;
          completedEl.hidden = false;
        }
        // 複数ファイルモード: 送信後に表のチェックや「ダウンロードリストに入れる」ボタンを押しても送れないので、押せなくする
        // （別の資料を受け取るにはページを読み込み直す。送り直せる形にするかは東さんの判断待ち）
        if (multi) {
          papers().forEach((p) => { p.disabled = true; });
          document.querySelectorAll("[data-register-lead-pick]").forEach((b) => { b.disabled = true; });
        }

        // createLead は 7 日以内の同メール重複を cookie で抑止 (旧版踏襲)
        const cookieKey = "modely_lead_submitted_" + encodeURIComponent(email.toLowerCase());
        if (!getCookie(cookieKey)) {
          try {
            await callCallable(CREATE_LEAD_URL, {
              name, email, phone, company, department,
              progress, timing,
              serviceName: SERVICE_NAME,
              paperTitle,
              utmSource, utmCampaign, utmMedium,
            });
          } catch (err) {
            // createLead 失敗はメール送信成功後の付随処理なので、ユーザーには見せず log のみ
            console.error("createLead failed", err);
          }
          setCookie(cookieKey, "1", 7, "/");
        }
      } catch (err) {
        alert("送信に失敗しました。入力内容を再度ご確認ください。それでも送信できない場合、大変お手数ですが 3d-modely@melta.co.jp までメールでお問い合わせください。");
        console.error(err);
        button.disabled = false;
        if (label) label.textContent = multi ? "選んだ資料をダウンロード" : "資料をダウンロード";
      } finally {
        sending = false;
        update();
      }
    });
  });
}
